import React, { useState, useEffect } from 'react';
import * as XLSX from 'xlsx';
import { db } from '../firebaseConfig/firebase';
import { collection, query, onSnapshot, doc, updateDoc } from "firebase/firestore";
import { Container, Badge, Button, Form, Row, Col, Spinner, Table, Card } from 'react-bootstrap';
import Header from './Header';

const Dashboard = () => {
    const [alumnos, setAlumnos] = useState([]);
    const [loading, setLoading] = useState(true);

    // Estado para controlar qué fila se está editando
    const [editId, setEditId] = useState(null);
    // Estado temporal para los valores del formulario de edición
    const [tempData, setTempData] = useState({});

    const [filtros, setFiltros] = useState({
        alumno: '',
        emailTribunal: '',
        tribunal: '',
        prioridad1: '',
        prioridad2: '',
        prioridad3: ''
    });

    const exportToExcel = () => {
        // Preparamos los datos: mapeamos el array para que las columnas tengan nombres legibles
        const datosAExportar = alumnosFiltrados.map(alumno => ({
            "Email Tribunal": alumno.EMAIL_Tribunal,
            "Alumno": alumno.ALUMNO,
            "Email Alumno": alumno.EMAIL_ALUMNO,
            "Tribunal": alumno.TRIBUNAL,
            "Nota Entrevista": alumno.NOTA_ENTREVISTA,
            "Nota Portfolio": alumno.NOTA_PORTFOLIO,
            "Nota Experiencia": alumno.NOTA_EXP,
            "Total": alumno.TOTAL_CANDIDATO,
            "Prioridad 1": alumno.PRIORIDAD_1,
            "Prioridad 2": alumno.PRIORIDAD_2,
            "Prioridad 3": alumno.PRIORIDAD_3,
            "Link Portfolio": alumno.PORTFOLIO,
            "Update Portfolio": alumno.PORTFOLI_UPDATE
        }));

        // Creamos el libro de trabajo (workbook) y la hoja (worksheet)
        const ws = XLSX.utils.json_to_sheet(datosAExportar);
        const wb = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(wb, ws, "Alumnos");

        // Generamos el archivo y disparamos la descarga
        XLSX.writeFile(wb, `Reporte_Alumnos_${new Date().toLocaleDateString()}.xlsx`);
    };

    useEffect(() => {
        const q = query(collection(db, "tribu-esp"));
        const unsubscribe = onSnapshot(q, (querySnapshot) => {
            const docs = [];
            querySnapshot.forEach((doc) => {
                docs.push({ ...doc.data(), id: doc.id });
            });
            setAlumnos(docs);
            setLoading(false);
        });
        return () => unsubscribe();
    }, []);

    const handleFilterChange = (e) => {
        setFiltros({ ...filtros, [e.target.name]: e.target.value });
    };

    // --- Lógica de Edición ---
    const startEditing = (alumno) => {
        setEditId(alumno.id);
        setTempData({ ...alumno });
    };

    const handleInputChange = (e) => {
        setTempData({ ...tempData, [e.target.name]: e.target.value });
    };

    const saveChanges = async (id) => {
        try {
            const nEntrevista = parseFloat(tempData.NOTA_ENTREVISTA) || 0;
            const nPortfolio = parseFloat(tempData.NOTA_PORTFOLIO) || 0;
            const nExp = parseFloat(tempData.NOTA_EXP) || 0;

            const nuevoTotal = ((nEntrevista + nPortfolio + nExp) / 3).toFixed(2);

            const alumnoRef = doc(db, "tribu-esp", id);
            await updateDoc(alumnoRef, {
                NOTA_ENTREVISTA: nEntrevista,
                NOTA_PORTFOLIO: nPortfolio,
                NOTA_EXP: nExp,
                TOTAL_CANDIDATO: nuevoTotal,
                PRIORIDAD_1: tempData.PRIORIDAD_1 || "",
                PRIORIDAD_2: tempData.PRIORIDAD_2 || "",
                PRIORIDAD_3: tempData.PRIORIDAD_3 || "",
                PORTFOLIO: tempData.PORTFOLIO || "",
                PORTFOLI_UPDATE: tempData.PORTFOLI_UPDATE || ""
            });
            setEditId(null);
        } catch (error) {
            console.error("Error al actualizar:", error);
            alert("Hubo un error al guardar.");
        }
    };

    const alumnosFiltrados = alumnos.filter((item) => {
        return (
            item.ALUMNO?.toLowerCase().includes(filtros.alumno.toLowerCase()) &&
            item.EMAIL_Tribunal?.toLowerCase().includes(filtros.emailTribunal.toLowerCase()) &&
            item.TRIBUNAL?.toLowerCase().includes(filtros.tribunal.toLowerCase()) &&
            item.PRIORIDAD_1?.toLowerCase().includes(filtros.prioridad1.toLowerCase()) &&
            item.PRIORIDAD_2?.toLowerCase().includes(filtros.prioridad2.toLowerCase()) &&
            item.PRIORIDAD_3?.toLowerCase().includes(filtros.prioridad3.toLowerCase())
        );
    });

    if (loading) return <Spinner animation="border" className="m-5" />;

    return (
        <>
            <Header />
            <Container fluid className="pt-4 bg-dark">
                
                {/* Panel de Filtros (Se mantiene igual) */}
                <Card bg="dark" text="white" className="mb-4 p-3 border-secondary">
                    <Form>
                        <Row className="d-flex justify-content-between align-items-center mb-4">
                            <Col xs="auto">
                                <h2 className="text-2xl text-secondary fw-bold tracking-tight text-slate-800 mb-0">
                                    Dashboard
                                </h2>
                            </Col>
                            <Col xs="auto">
                                <Button variant="success" onClick={exportToExcel}>
                                    <i className="bi bi-file-earmark-excel"></i> Exportar
                                </Button>
                            </Col>
                        </Row>
                        <Row>
                            <Col md={4} className="mb-2">
                                <Form.Group>
                                    <Form.Label><h5>Buscar Alumno</h5></Form.Label>
                                    <Form.Control name="alumno" placeholder="Nombre..." onChange={handleFilterChange} className="bg-secondary text-white border-0" />
                                </Form.Group>
                            </Col>
                            <Col md={4} className="mb-2">
                                <Form.Group>
                                    <Form.Label><h5>Email Tribunal</h5></Form.Label>
                                    <Form.Control name="emailTribunal" placeholder="Email..." onChange={handleFilterChange} className="bg-secondary text-white border-0" />
                                </Form.Group>
                            </Col>
                            <Col md={4} className="mb-2">
                                <Form.Group>
                                    <Form.Label><h5>Tribunal</h5></Form.Label>
                                    <Form.Control name="tribunal" placeholder="Filtrar por tribunal..." onChange={handleFilterChange} className="bg-secondary text-white border-0" />
                                </Form.Group>
                            </Col>
                        </Row>
                        <Row>
                            <Col md={4}><Form.Control name="prioridad1" placeholder="Prioridad 1..." size="sm" onChange={handleFilterChange} className="bg-secondary text-white border-0 mb-2" /></Col>
                            <Col md={4}><Form.Control name="prioridad2" placeholder="Prioridad 2..." size="sm" onChange={handleFilterChange} className="bg-secondary text-white border-0 mb-2" /></Col>
                            <Col md={4}><Form.Control name="prioridad3" placeholder="Prioridad 3..." size="sm" onChange={handleFilterChange} className="bg-secondary text-white border-0 mb-2" /></Col>
                        </Row>
                    </Form>
                </Card>

                <Table striped bordered hover responsive variant="dark" size="sm">
                    <thead>
                        <tr className="text-center">
                            <th>Email Tribu</th>
                            <th>Alumno</th>
                            <th>Email</th>
                            <th>Tribunal</th>
                            <th>Ent</th>
                            <th>Port</th>
                            <th>Exp</th>
                            <th>Total</th>
                            <th>P1</th>
                            <th>P2</th>
                            <th>P3</th>
                            <th>Portfolio</th>
                            <th>Update</th>
                            <th>Acciones</th>
                        </tr>
                    </thead>
                    <tbody>
                        {alumnosFiltrados.map((alumno) => (
                            <tr key={alumno.id} className="text-center">
                                {/* CAMPOS DE SOLO LECTURA */}
                                <td><small>{alumno.EMAIL_Tribunal}</small></td>
                                <td><small>{alumno.ALUMNO}</small></td>
                                <td><small>{alumno.EMAIL_ALUMNO}</small></td>
                                <td><Badge bg="warning" className="border-2  text-dark">{alumno.TRIBUNAL}</Badge></td>

                                {editId === alumno.id ? (
                                    <>
                                        {/* CAMPOS EDITABLES */}
                                        <td><Form.Control size="sm" name="NOTA_ENTREVISTA" value={tempData.NOTA_ENTREVISTA} onChange={handleInputChange} /></td>
                                        <td><Form.Control size="sm" name="NOTA_PORTFOLIO" value={tempData.NOTA_PORTFOLIO} onChange={handleInputChange} /></td>
                                        <td><Form.Control size="sm" name="NOTA_EXP" value={tempData.NOTA_EXP} onChange={handleInputChange} /></td>
                                        <td className="fw-bold text-warning">
                                            {((parseFloat(tempData.NOTA_ENTREVISTA || 0) + parseFloat(tempData.NOTA_PORTFOLIO || 0) + parseFloat(tempData.NOTA_EXP || 0)) / 3).toFixed(2)}
                                        </td>
                                        <td><Form.Control size="sm" name="PRIORIDAD_1" value={tempData.PRIORIDAD_1} onChange={handleInputChange} /></td>
                                        <td><Form.Control size="sm" name="PRIORIDAD_2" value={tempData.PRIORIDAD_2} onChange={handleInputChange} /></td>
                                        <td><Form.Control size="sm" name="PRIORIDAD_3" value={tempData.PRIORIDAD_3} onChange={handleInputChange} /></td>
                                        <td><Form.Control size="sm" name="PORTFOLIO" value={tempData.PORTFOLIO} onChange={handleInputChange} /></td>
                                        <td><Form.Control size="sm" name="PORTFOLI_UPDATE" value={tempData.PORTFOLI_UPDATE} onChange={handleInputChange} /></td>
                                        <td className="text-center">
                                            <Button variant="success" size="sm" onClick={() => saveChanges(alumno.id)}>✓</Button>
                                            <Button variant="danger" size="sm" className="ms-1" onClick={() => setEditId(null)}>✕</Button>
                                        </td>
                                    </>
                                ) : (
                                    <>
                                        {/* VISTA NORMAL (LECTURA) */}
                                        <td>{alumno.NOTA_ENTREVISTA}</td>
                                        <td>{alumno.NOTA_PORTFOLIO}</td>
                                        <td>{alumno.NOTA_EXP}</td>
                                        <td className="fw-bold text-warning">{alumno.TOTAL_CANDIDATO}</td>
                                        <td><small>{alumno.PRIORIDAD_1}</small></td>
                                        <td><small>{alumno.PRIORIDAD_2}</small></td>
                                        <td><small>{alumno.PRIORIDAD_3}</small></td>
                                        <td>
                                            <a href={alumno.PORTFOLIO} target="_blank" rel="noreferrer" className="btn btn-sm btn-outline-light">Ver</a>
                                        </td>
                                        <td>
                                            {alumno.PORTFOLI_UPDATE && <a href={alumno.PORTFOLI_UPDATE} target="_blank" rel="noreferrer" className="btn btn-sm btn-outline-light">Ver</a>}
                                        </td>
                                        <td className="text-center">
                                            {/*<Button variant="outline-warning" size="sm" onClick={() => startEditing(alumno)}>Editar</Button>*/}
                                        </td>
                                    </>
                                )}
                            </tr>
                        ))}
                    </tbody>
                </Table>
            </Container>
        </>
    );
};

export default Dashboard;